import {match} from 'ts-pattern';

import {isNil, omit} from 'ramda';

import {FinancingCompositionLayer} from '@dms/api/shared';

/**
 * Merges a partial patch into a layer while preserving its discriminant, so an update can never
 * turn one layer type into another.
 */
export const applyLayerPatch = (
  layer: FinancingCompositionLayer,
  patch: Partial<FinancingCompositionLayer>
): FinancingCompositionLayer =>
  match(layer)
    .with({type: 'text'}, (textLayer) => ({...textLayer, ...patch, type: 'text' as const}))
    .with({type: 'image'}, (imageLayer) => ({...imageLayer, ...patch, type: 'image' as const}))
    .with({type: 'rect'}, (rectLayer) => ({...rectLayer, ...patch, type: 'rect' as const}))
    // `fit` is shared with image layers but narrower here; a foreign 'fill' keeps the current value.
    .with({type: 'photo'}, (photoLayer) => {
      const fit = 'fit' in patch ? patch.fit : undefined;
      // A patched `crop: undefined` resets the crop. The key is dropped, never kept undefined:
      // the BE accepts only a missing key for "no crop".
      const crop = 'crop' in patch ? patch.crop : photoLayer.crop;
      const patched = {
        ...photoLayer,
        ...patch,
        crop,
        fit: fit === 'contain' || fit === 'cover' ? fit : photoLayer.fit,
        type: 'photo' as const,
      };
      return isNil(crop) ? omit(['crop'], patched) : patched;
    })
    .exhaustive();
